import React, { useState } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import CredentialService from "../../../_services/CredentialService";
import VoilaBoardService from "../../../_services/VoilaBoardService";
import PhotoGalleryService from "../../../_services/PhotoGalleryService";

interface Props {
  show: boolean;
  photoIds: number[];
  onHide: () => void;
  onDone: (message: string) => void;
}

interface StudentResult {
  enquiryId: number;
  studentName: string;
  uid: string;
}

interface Board {
  id: number;
  title: string;
}

const ShareToVoilaBoardDialog: React.FC<Props> = ({
  show,
  photoIds,
  onHide,
  onDone,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [students, setStudents] = useState<StudentResult[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentResult | null>(
    null,
  );

  const [boards, setBoards] = useState<Board[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);

  const [saving, setSaving] = useState(false);

  const resetState = () => {
    setSearchText("");
    setStudents([]);
    setSelectedStudent(null);
    setBoards([]);
    setSelectedBoardId(null);
    setSaving(false);
  };

  const handleSearch = async () => {
    if (searchText.trim().length < 2) return;
    setSearching(true);
    setSelectedStudent(null);
    setBoards([]);
    setSelectedBoardId(null);
    try {
      const res: any = await CredentialService.searchStudents(searchText.trim());
      setStudents(res.data?.data || []);
    } catch (err) {
      console.error("Student search failed", err);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStudent = async (student: StudentResult) => {
    setSelectedStudent(student);
    setLoadingBoards(true);
    setSelectedBoardId(null);
    try {
      const res: any = await VoilaBoardService.listBoards(student.enquiryId);
      const boardList = res.data?.data || [];
      setBoards(boardList);
      if (boardList.length === 1) {
        setSelectedBoardId(boardList[0].id);
      }
    } catch (err) {
      console.error("Failed to load boards", err);
    } finally {
      setLoadingBoards(false);
    }
  };

  const handleShare = async () => {
    if (!selectedBoardId) return;
    setSaving(true);
    try {
      const res: any = await PhotoGalleryService.shareToVoilaBoard(
        photoIds,
        selectedBoardId,
      );
      const msg = res.data?.message || "Photos shared to VoilaBoard";
      resetState();
      onDone(msg);
    } catch (err) {
      console.error("Failed to share to VoilaBoard", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        resetState();
        onHide();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Share to VoilaBoard
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Step 1: Search student */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
            Search Student
          </Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              size="sm"
              type="text"
              placeholder="Student name or ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSearch}
              disabled={searching || searchText.trim().length < 2}
              style={{ whiteSpace: "nowrap" }}
            >
              {searching ? <Spinner animation="border" size="sm" /> : "Search"}
            </Button>
          </div>
        </Form.Group>

        {/* Student results */}
        {students.length > 0 && !selectedStudent && (
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            {students.map((s) => (
              <div
                key={s.enquiryId}
                onClick={() => handleSelectStudent(s)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--border-subtle)",
                  fontSize: 13,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f0f5ff")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div style={{ fontWeight: 600 }}>{s.studentName}</div>
                <div style={{ fontSize: 11, color: "#868e96" }}>{s.uid}</div>
              </div>
            ))}
          </div>
        )}

        {students.length === 0 && searchText.length >= 2 && !searching && !selectedStudent && (
          <p className="text-muted" style={{ fontSize: 13 }}>
            No students found.
          </p>
        )}

        {/* Selected student chip */}
        {selectedStudent && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#e7f1ff",
              borderRadius: 16,
              padding: "4px 12px",
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {selectedStudent.studentName}
            </span>
            <span
              style={{ cursor: "pointer", color: "var(--status-danger)", fontWeight: 700 }}
              onClick={() => {
                setSelectedStudent(null);
                setBoards([]);
                setSelectedBoardId(null);
              }}
            >
              x
            </span>
          </div>
        )}

        {/* Step 2: Pick board */}
        {selectedStudent && (
          <Form.Group className="mt-2">
            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
              Select Board
            </Form.Label>
            {loadingBoards ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
              </div>
            ) : boards.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 13 }}>
                No VoilaBoards found for this student.
              </p>
            ) : (
              <Form.Select
                size="sm"
                value={selectedBoardId || ""}
                onChange={(e) =>
                  setSelectedBoardId(Number(e.target.value) || null)
                }
              >
                {boards.length > 1 && (
                  <option value="">Choose a board...</option>
                )}
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title || `Board #${b.id}`}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            resetState();
            onHide();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleShare}
          disabled={!selectedBoardId || saving}
        >
          {saving
            ? "Sharing..."
            : `Share ${photoIds.length} Photo(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareToVoilaBoardDialog;
